import { DateTime } from "luxon";
import { AppStore } from "@/store/AppStore";
import { SearchSuggestion, SearchSuggestionType } from "@/domains/search";
import { generateRecentDateString } from "@/domains/date/date";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { INoteObservable } from "@/store/note";
import { MDSComboboxItem } from "@/mds/components/forms";
import { MemCommonMentionKind } from "@mem-labs/common-editor";
import { logger } from "@/modules/logger";

/**
 * Note/collection @-mention search for session composers — recents on an
 * empty query, search suggestions otherwise, grouped under headers. Shared
 * by session composers.
 */
export const fetchSessionComboboxData = async ({
  store,
  query,
  mentionSymbol,
}: {
  store: AppStore;
  query: string | undefined;
  mentionSymbol: string | undefined;
}): Promise<{ data: MDSComboboxItem[]; isLoading: boolean }> => {
  try {
    // The recents store lazily starts its liveQuery on first MobX
    // observation; this fetch reads it outside any reactive context, so kick
    // it explicitly (idempotent) or windows without recents UI (the floating
    // widget) would always see empty recents.
    store.recentItems.initialize();

    const suggestionType =
      mentionSymbol === "@"
        ? SearchSuggestionType.NOTE
        : mentionSymbol === "#"
          ? SearchSuggestionType.COLLECTION
          : undefined;

    // Handle empty query: return recents
    if (!query) {
      if (suggestionType) {
        const recentItems = await Promise.all(
          (suggestionType === SearchSuggestionType.NOTE
            ? store.recentItems.sortedRecentNotesInteractedWithByMe
            : store.recentItems.sortedRecentCollectionsInteractedWithByMe
          )
            .filter(item => item.isAvailable)
            .slice(0, 5)
            .map(item => processRecentItem(item))
        );

        const data = recentItems.length > 0 ? [createHeader(suggestionType), ...recentItems] : [];

        return { data, isLoading: false };
      } else {
        const recentNotes = await Promise.all(
          store.recentItems.sortedRecentNotesInteractedWithByMe
            .filter(item => item.isAvailable)
            .slice(0, 5)
            .map(item => processRecentItem(item))
        );

        const recentCollections = await Promise.all(
          store.recentItems.sortedRecentCollectionsInteractedWithByMe
            .filter(item => item.isAvailable)
            .slice(0, 5)
            .map(item => processRecentItem(item))
        );

        return {
          data: buildDataWithHeaders(recentNotes, recentCollections),
          isLoading: false,
        };
      }
    }

    // Handle search query
    const andCond = suggestionType
      ? (item: SearchSuggestion) => item.type === suggestionType
      : undefined;

    const suggestions = await store.search.forSuggestions(query, "mentions", andCond, 5);

    if (suggestionType) {
      const searchResults = await Promise.all(
        suggestions.map(suggestion => processSearchSuggestion(store, suggestion))
      );
      const data = searchResults.length > 0 ? [createHeader(suggestionType), ...searchResults] : [];

      return { data, isLoading: false };
    } else {
      const notesSuggestions = suggestions.filter(s => s.type === SearchSuggestionType.NOTE);
      const collectionsSuggestions = suggestions.filter(
        s => s.type === SearchSuggestionType.COLLECTION
      );

      const notesData = await Promise.all(
        notesSuggestions.map(suggestion => processSearchSuggestion(store, suggestion))
      );
      const collectionsData = await Promise.all(
        collectionsSuggestions.map(suggestion => processSearchSuggestion(store, suggestion))
      );

      return {
        data: buildDataWithHeaders(notesData, collectionsData),
        isLoading: false,
      };
    }
  } catch (error) {
    logger.error({
      message: "Failed to fetch combobox data",
      info: {
        error: error instanceof Error ? error.message : String(error),
        query: String(query),
      },
    });
    return { data: [], isLoading: false };
  }
};

const createHeader = (type: SearchSuggestionType): MDSComboboxItem => ({
  id: type === SearchSuggestionType.NOTE ? "notes-header" : "collections-header",
  label: type === SearchSuggestionType.NOTE ? "Notes" : "Collections",
  kind:
    type === SearchSuggestionType.NOTE
      ? MemCommonMentionKind.Note
      : MemCommonMentionKind.Collection,
  isHeader: true,
});

const prepareNoteMetadata = (note: INoteObservable) => {
  return {
    isShared: note.isShared,
    dateLabel: generateRecentDateString(
      DateTime.fromISO(
        note.lastMentionedAt || note.lastViewedAt || note.receivedAt || note.locallyCreatedAt || ""
      ),
      { skipFullDayName: true }
    ),
  };
};

const prepareCollectionMetadata = async (collection: CollectionObservable) => {
  const noteCount = (await collection.itemList.getAllCollectionItemIdsAsync()).length;
  return {
    collectionId: collection.id,
    isShared: collection.isShared,
    description: collection.description,
    noteCount,
  };
};

const processRecentItem = async (
  item: INoteObservable | CollectionObservable
): Promise<MDSComboboxItem> => ({
  id: item.id,
  label: item instanceof CollectionObservable ? item.label : item.title,
  kind:
    item instanceof CollectionObservable
      ? MemCommonMentionKind.Collection
      : MemCommonMentionKind.Note,
  metadata: {
    ...(item instanceof CollectionObservable
      ? await prepareCollectionMetadata(item)
      : prepareNoteMetadata(item)),
  },
});

const processSearchSuggestion = async (
  store: AppStore,
  suggestion: SearchSuggestion
): Promise<MDSComboboxItem> => {
  const item =
    suggestion.type === SearchSuggestionType.NOTE
      ? await store.notes.getAsync(suggestion.modelId)
      : await store.collections.getAsync(suggestion.modelId);

  return {
    id: suggestion.modelId,
    label: suggestion.label || suggestion.modelId,
    value: suggestion.label || suggestion.modelId,
    kind:
      suggestion.type === SearchSuggestionType.NOTE
        ? MemCommonMentionKind.Note
        : MemCommonMentionKind.Collection,
    metadata: {
      ...(item instanceof CollectionObservable
        ? await prepareCollectionMetadata(item)
        : prepareNoteMetadata(item as INoteObservable)),
    },
  };
};

const buildDataWithHeaders = (
  notesData: MDSComboboxItem[],
  collectionsData: MDSComboboxItem[]
): MDSComboboxItem[] => {
  const data: MDSComboboxItem[] = [];

  if (notesData.length > 0) {
    data.push(createHeader(SearchSuggestionType.NOTE));
    data.push(...notesData);
  }

  if (collectionsData.length > 0) {
    data.push(createHeader(SearchSuggestionType.COLLECTION));
    data.push(...collectionsData);
  }

  return data;
};
